import { z } from 'zod';

/**
 * DTO di lettura della pagina Chiamate live / Live coaching (SPA tenant).
 * Source of truth: `packages/backend/src/routes/crm/coaching.routes.ts`
 * (card RTA, statistiche, trend) e `telephony/voip-routes.ts`
 * (`GET /voip/calls/active`).
 */

/** `GET|PUT /coaching/settings` → `data` (default di dominio se la riga manca). */
export const coachingSentimentSettingsSchema = z.object({
  sentiment_mode: z.string(),
  warning_threshold: z.number(),
  critical_threshold: z.number(),
  emit_interval_seconds: z.number(),
  notify_on_critical: z.boolean(),
});
export type CoachingSentimentSettings = z.infer<typeof coachingSentimentSettingsSchema>;

/** `POST /calls/hangup-all` → `data`: conteggio chiamate terminate/tentate. */
export const hangupAllCallsResultSchema = z.object({
  terminated: z.number(),
  total: z.number(),
  memoryCleared: z.number().optional(),
  message: z.string().optional(),
});
export type HangupAllCallsResult = z.infer<typeof hangupAllCallsResultSchema>;

/** `GET /voip/calls/active` → `data[]`: chiamata live normalizzata dal backend. */
export const voipActiveCallListItemSchema = z.object({
  uuid: z.string(),
  caller_number: z.string(),
  caller_name: z.string().nullable().optional(),
  callee_number: z.string(),
  callee_name: z.string().nullable().optional(),
  direction: z.enum(['inbound', 'outbound', 'internal']),
  status: z.enum(['ringing', 'queued', 'answered', 'on_hold', 'bridged', 'muted']),
  start_time: z.string(),
  answer_time: z.string().nullable().optional(),
  duration: z.number(),
  queue_name: z.string().nullable().optional(),
  extension: z.string().nullable().optional(),
  answered_by_extension: z.string().nullable().optional(),
  recording: z.boolean().optional(),
});
export type VoipActiveCallListItem = z.infer<typeof voipActiveCallListItemSchema>;

/** Riga `rta_cards` (`GET /coaching/cards`, `POST|PUT /coaching/cards/:id`). */
export const coachingRtaCardSchema = z.object({
  id: z.string(),
  title: z.string(),
  content: z.string(),
  content_markdown: z.string().nullable().optional(),
  category: z.string(),
  priority: z.number(),
  keywords: z.array(z.string()),
  keyword_mode: z.enum(['any', 'all', 'exact', 'regex']),
  language: z.string(),
  target_queues: z.array(z.string()).nullable().optional(),
  rag_enabled: z.boolean().optional(),
  is_active: z.boolean(),
  cooldown_seconds: z.number(),
  created_at: z.string().optional(),
});
export type CoachingRtaCard = z.infer<typeof coachingRtaCardSchema>;

/** `GET /coaching/stats` → `data`. */
export const coachingStatsSchema = z.object({
  period_days: z.number(),
  triggers: z.object({ total_triggers: z.number(), calls_with_triggers: z.number() }),
  sentiment: z.object({
    total_logs: z.number(),
    avg_score: z.number().nullable(),
    positive_count: z.number(),
    neutral_count: z.number(),
    negative_count: z.number(),
    critical_count: z.number().optional(),
  }),
  top_cards: z.array(z.object({ id: z.string(), title: z.string(), category: z.string(), trigger_count: z.number() })),
  top_keywords: z.array(z.object({ matched_keyword: z.string(), hit_count: z.number() })).optional(),
});
export type CoachingStats = z.infer<typeof coachingStatsSchema>;

/** `GET /coaching/stats/trend` → `data`. */
export const coachingTrendSchema = z.object({
  period_days: z.number(),
  sentiment_trend: z.array(z.object({
    day: z.string(),
    hour: z.number(),
    agent_extension: z.string().nullable(),
    queue_id: z.string().nullable(),
    avg_score: z.number(),
    log_count: z.number(),
    critical_count: z.number(),
  })),
  trigger_heatmap: z.array(z.object({ hour: z.number(), day_of_week: z.number(), trigger_count: z.number() })),
});
export type CoachingTrend = z.infer<typeof coachingTrendSchema>;
